import React from 'react';
import { connect } from 'react-redux';
import {
  Row,
  Col,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from 'reactstrap';

import { callApi, formatDate } from '../../utils';
import { showError, showInfo } from '../../actions/notificationActions';
import Loading from '../Loading';
import DataTable from '../DataTable';

// Fixed domain enum — there is no endpoint to fetch this list, so it is
// hardcoded on the frontend (see backend contract for /admin/users/:id/accesses).
const DOMAINS = ['CLIENT', 'ADMIN', 'MERCHANT', 'AGENT'];

const ROLES_MAX = 45;

const isApprovalRequired = (err) =>
  err &&
  typeof err === 'object' &&
  (err.__approvalHandled || err.status === 'approval-required');

class UserAccess extends React.Component {
  state = {
    loading: false,
    error: null,
    rows: [],

    // Grant modal
    grantOpen: false,
    grantLoading: false,
    grantForm: { domain: '', roles: '' },
    grantErrors: {},

    // Revoke modal
    revokeOpen: false,
    revokeLoading: false,
    revokeTarget: null,
  };

  componentWillMount() {
    this.loadAccesses();
  }

  loadAccesses = () => {
    const id = this.props.user.id;
    this.setState({ loading: true, error: null });
    callApi(`/users/${id}/accesses`)
      .then(({ data = [] }) => {
        this.setState({ loading: false, rows: data });
      })
      .catch((err) => {
        this.setState({
          loading: false,
          error: (err && err.message) || 'Unable to load access records.',
        });
        this.props.dispatch(showError(err));
      });
  };

  // ---- Grant flow -----------------------------------------------------------

  openGrant = () => {
    this.setState({
      grantOpen: true,
      grantForm: { domain: '', roles: '' },
      grantErrors: {},
    });
  };

  closeGrant = () => {
    if (this.state.grantLoading) return;
    this.setState({ grantOpen: false });
  };

  setGrantInput = (e) => {
    const { name, value } = e.target;
    this.setState((state) => ({
      grantForm: { ...state.grantForm, [name]: value },
      grantErrors: { ...state.grantErrors, [name]: undefined },
    }));
  };

  validateGrant = () => {
    const errors = {};
    const domain = this.state.grantForm.domain;
    const roles = (this.state.grantForm.roles || '').trim();

    if (!DOMAINS.includes(domain)) {
      errors.domain = 'Select a valid domain.';
    }
    if (!roles) {
      errors.roles = 'Roles is required.';
    } else if (roles.length > ROLES_MAX) {
      errors.roles = `Roles must be ${ROLES_MAX} characters or fewer.`;
    }
    return errors;
  };

  submitGrant = (e) => {
    e.preventDefault();
    const errors = this.validateGrant();
    if (Object.keys(errors).length) {
      this.setState({ grantErrors: errors });
      return;
    }

    const id = this.props.user.id;
    const body = {
      domain: this.state.grantForm.domain,
      roles: this.state.grantForm.roles.trim(),
    };

    this.setState({ grantLoading: true });
    callApi(`/users/${id}/accesses`, body, 'post')
      .then((res) => {
        // A 2xx here means the request was captured for approval (not applied).
        // Do NOT optimistically add the row — the list only reflects applied state.
        this.setState({ grantLoading: false, grantOpen: false });
        this.props.dispatch(showInfo(res.message));
      })
      .catch((err) => {
        this.setState({ grantLoading: false });
        if (isApprovalRequired(err)) {
          // The interceptor already opened the approval modal; close ours so it
          // is visible and confirm the pending state textually.
          this.setState({ grantOpen: false });
          this.props.dispatch(showInfo(err.message));
          return;
        }
        // Genuine error (validation, 401/403 insufficient permission, etc.) —
        // keep the modal open so the admin can correct and retry.
        this.props.dispatch(showError(err));
      });
  };

  // ---- Revoke flow ----------------------------------------------------------

  openRevoke = (access) => {
    this.setState({ revokeOpen: true, revokeTarget: access });
  };

  closeRevoke = () => {
    if (this.state.revokeLoading) return;
    this.setState({ revokeOpen: false, revokeTarget: null });
  };

  confirmRevoke = () => {
    const id = this.props.user.id;
    const domain = this.state.revokeTarget && this.state.revokeTarget.domain;
    if (!domain) return;

    this.setState({ revokeLoading: true });
    callApi(`/users/${id}/accesses/${domain}`, {}, 'delete')
      .then((res) => {
        // Captured for approval — do NOT optimistically remove the row.
        this.setState({
          revokeLoading: false,
          revokeOpen: false,
          revokeTarget: null,
        });
        this.props.dispatch(showInfo(res.message));
      })
      .catch((err) => {
        this.setState({ revokeLoading: false });
        if (isApprovalRequired(err)) {
          this.setState({ revokeOpen: false, revokeTarget: null });
          this.props.dispatch(showInfo(err.message));
          return;
        }
        this.setState({ revokeOpen: false, revokeTarget: null });
        this.props.dispatch(showError(err));
      });
  };

  // ---- Render ---------------------------------------------------------------

  renderBody() {
    const { loading, error, rows } = this.state;

    if (loading) {
      return <Loading />;
    }

    if (error) {
      return (
        <div className="text-center" style={{ padding: '30px 0' }}>
          <p className="text-muted">{error}</p>
          <Button color="primary" size="sm" onClick={this.loadAccesses}>
            Retry
          </Button>
        </div>
      );
    }

    if (!rows.length) {
      return (
        <div className="text-center text-muted" style={{ padding: '30px 0' }}>
          No access granted yet.
        </div>
      );
    }

    return (
      <DataTable card={false} loading={loading} minWidth={700}>
        <thead className="text-primary">
          <tr>
            <th>Domain</th>
            <th>Roles</th>
            <th>Active</th>
            <th>Created</th>
            <th>Updated</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => {
            const active = !!a.active;
            return (
              <tr key={a.id}>
                <td>
                  <Badge color="info">{a.domain}</Badge>
                </td>
                <td>{a.roles}</td>
                <td>
                  <Badge color={active ? 'success' : 'secondary'}>
                    {active ? 'Yes' : 'No'}
                  </Badge>
                </td>
                <td>{a.created_at ? formatDate(a.created_at) : '—'}</td>
                <td>{a.updated_at ? formatDate(a.updated_at) : '—'}</td>
                <td>
                  <Button
                    size="sm"
                    color="danger"
                    onClick={() => this.openRevoke(a)}
                  >
                    Revoke
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </DataTable>
    );
  }

  renderGrantModal() {
    const { grantOpen, grantLoading, grantForm, grantErrors, rows } =
      this.state;

    // Optional nicety: domains the user already has an active access for cannot
    // be granted again.
    const activeDomains = rows.filter((a) => !!a.active).map((a) => a.domain);

    return (
      <Modal isOpen={grantOpen} toggle={this.closeGrant}>
        <ModalHeader toggle={this.closeGrant}>Grant access</ModalHeader>
        <Form onSubmit={this.submitGrant}>
          <ModalBody>
            <FormGroup>
              <Label for="access-domain">Domain</Label>
              <Input
                type="select"
                name="domain"
                id="access-domain"
                value={grantForm.domain}
                onChange={this.setGrantInput}
                invalid={!!grantErrors.domain}
              >
                <option value="">Select a domain…</option>
                {DOMAINS.map((d) => (
                  <option
                    key={d}
                    value={d}
                    disabled={activeDomains.includes(d)}
                  >
                    {d}
                    {activeDomains.includes(d) ? ' (already granted)' : ''}
                  </option>
                ))}
              </Input>
              <FormFeedback>{grantErrors.domain}</FormFeedback>
            </FormGroup>

            <FormGroup>
              <Label for="access-roles">Roles</Label>
              <Input
                type="text"
                name="roles"
                id="access-roles"
                placeholder="e.g. admin"
                maxLength={ROLES_MAX}
                value={grantForm.roles}
                onChange={this.setGrantInput}
                invalid={!!grantErrors.roles}
              />
              <FormFeedback>{grantErrors.roles}</FormFeedback>
              <small className="text-muted">
                {(grantForm.roles || '').length}/{ROLES_MAX}
              </small>
            </FormGroup>

            <small className="text-muted">
              Granting access requires approval from a second admin before it
              takes effect.
            </small>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={this.closeGrant}
              disabled={grantLoading}
            >
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={grantLoading}>
              {grantLoading ? 'Please wait…' : 'Submit for approval'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    );
  }

  renderRevokeModal() {
    const { revokeOpen, revokeLoading, revokeTarget } = this.state;
    const domain = revokeTarget && revokeTarget.domain;

    return (
      <Modal isOpen={revokeOpen} toggle={this.closeRevoke}>
        <ModalHeader toggle={this.closeRevoke}>Revoke access</ModalHeader>
        <ModalBody>
          <p>
            Revoke <Badge color="info">{domain}</Badge> access for this user?
          </p>
          <small className="text-muted">
            This requires approval from a second admin before it takes effect.
          </small>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={this.closeRevoke}
            disabled={revokeLoading}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={this.confirmRevoke}
            disabled={revokeLoading}
          >
            {revokeLoading ? 'Please wait…' : 'Submit for approval'}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  render() {
    return (
      <div>
        <Row>
          <Col md="12" xs={12}>
            <div className="mt-10">
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginBottom: 10,
                }}
              >
                <Button color="primary" size="sm" onClick={this.openGrant}>
                  Grant access
                </Button>
              </div>
              {this.renderBody()}
            </div>
          </Col>
        </Row>
        {this.renderGrantModal()}
        {this.renderRevokeModal()}
      </div>
    );
  }
}

export default connect()(UserAccess);
